import {
  type InfiniteData,
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/vue-query";
import axios from "axios";
import { camelCase } from "lodash-es";
import cloneDeep from "lodash-es/cloneDeep";
import {
  type EventsOverviewList,
  MePreferenceProductsApi,
  type ProductList,
  type ProductPreferences,
  type SearchAll,
} from "mws-ts-v2-sdk";

import type { EventFollowedType } from "~/analytics/segment";
import { queries } from "~/queries";

import { preferencesQueryKeys } from "./preferences";

const getProductPreferencesApi = () => {
  return new MePreferenceProductsApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    axios,
  );
};

const setProductFollowStatus = (
  productId: string,
  isFollowedByUser: boolean,
  queryClient: QueryClient,
) => {
  queryClient.setQueriesData<InfiniteData<ProductList>>(
    { queryKey: ["products", "infiniteList"] },
    (data) => {
      if (!data) return data;

      data.pages.forEach((page, pageIndex) => {
        page.results.forEach((result, resultIndex) => {
          if (result.id === productId) {
            data.pages[pageIndex]!.results[resultIndex]!.isFollowedByUser =
              isFollowedByUser;
          }
        });
      });

      return cloneDeep(data);
    },
  );

  queryClient.setQueriesData<InfiniteData<EventsOverviewList>>(
    { queryKey: ["events", "overview", "infiniteList"] },
    (data) => {
      if (!data || !data.pages.length) return data;

      data.pages.forEach((page, pageIndex) => {
        page.results.forEach((result, resultIndex) => {
          result.products.forEach((product, productIndex) => {
            if (product.id === productId) {
              data.pages[pageIndex]!.results[resultIndex]!.products[
                productIndex
              ]!.isFollowedByUser = isFollowedByUser;
            }
          });
        });
      });

      return cloneDeep(data);
    },
  );

  queryClient.setQueriesData<SearchAll>(
    { queryKey: queries.search.all._def },
    (data) => {
      if (!data) return data;

      const productIndex = data.products.findIndex(
        ({ id }) => id === productId,
      );
      if (productIndex === -1 || productIndex === undefined) return data;

      data.products[productIndex]!.isFollowedByUser = isFollowedByUser;

      return cloneDeep(data);
    },
  );

  queryClient.setQueriesData<ProductList>(
    { queryKey: queries.search.products._def },
    (data) => {
      if (!data) return data;

      const productIndex = data.results.findIndex(
        (product) => product?.id === productId,
      );

      if (productIndex === -1 || productIndex === undefined) return data;

      data.results[productIndex]!.isFollowedByUser = isFollowedByUser;

      return cloneDeep(data);
    },
  );

  queryClient.setQueriesData<InfiniteData<ProductList>>(
    { queryKey: ["search", "products", "infiniteList"] },
    (data) => {
      if (!data) return data;

      data.pages.forEach((page, pageIndex) => {
        const productIndex = page.results.findIndex(
          ({ id }) => id === productId,
        );

        if (productIndex > -1) {
          data.pages[pageIndex]!.results[productIndex]!.isFollowedByUser =
            isFollowedByUser;
        }
      });

      return cloneDeep(data);
    },
  );

  queryClient.setQueriesData<ProductPreferences>(
    { queryKey: queries.followedProducts.list._def },
    (data) => {
      if (!data) return data;

      const productIndex = data?.results?.findIndex(
        (product) => product.id === productId,
      );
      if (productIndex === -1 || productIndex === undefined) return data;

      data.results[productIndex]!.followed = isFollowedByUser;

      return cloneDeep(data);
    },
  );

  queryClient.setQueriesData<{
    [key: string]: boolean;
  }>({ queryKey: queries.followedProducts.status._def }, (data) => {
    if (!data) return data;

    // camelCase is needed due to our axios interceptor that transforms keys
    const camelCaseProductId = camelCase(productId);

    if (data[camelCaseProductId] !== undefined) {
      data[camelCaseProductId] = isFollowedByUser;
    }

    return cloneDeep(data);
  });
};

type TrackData = {
  followType: EventFollowedType;
};

export const useFollowProductMutation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useI18n();

  const { productFollowed } = useAnalytics();
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const mutationFn = async (request: {
    id: string;
    newFollowStatus: boolean;
    trackData: TrackData;
  }) => {
    const response =
      await getProductPreferencesApi().v2MePreferencesProductsProductIdPut({
        productId: request.id,
        v2MePreferencesCategoriesCategoryIdPutRequest: {
          follow: request.newFollowStatus,
        },
      });

    return response.data;
  };

  return useMutation({
    mutationFn,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      try {
        await Promise.allSettled([
          queryClient.cancelQueries({
            queryKey: ["products", "infiniteList"],
          }),
          queryClient.cancelQueries({
            queryKey: ["events", "overview", "infiniteList"],
          }),
          queryClient.cancelQueries({
            queryKey: queries.followedProducts.list._def,
          }),
        ]);
      } catch {
        // fail silently
      }

      // Optimistically update to the new value
      setProductFollowStatus(
        variables.id,
        variables.newFollowStatus,
        queryClient,
      );
    },
    onError: (_, variables) => {
      toast.clear();
      toast.error(t("customerPreferences.followFeedback.error"));

      // Rollback the optimistic update
      setProductFollowStatus(
        variables.id,
        !variables.newFollowStatus,
        queryClient,
      );
    },
    onSuccess: async (_, variables) => {
      // Grab the product for the categories
      const product = await queryClient.ensureQueryData(
        queries.products.details(variables.id, platformId),
      );

      productFollowed(variables.newFollowStatus ? "follow" : "unfollow", {
        productId: variables.id,
        productName: product.name,
        priceInEuros: getPriceInCurrency(product.prices, "EUR"),
        type: variables.trackData.followType,
        categories: product.categories,
      });

      setProductFollowStatus(
        variables.id,
        variables.newFollowStatus,
        queryClient,
      );

      queryClient.invalidateQueries({
        queryKey: preferencesQueryKeys.list._def,
      });

      queryClient.invalidateQueries({
        queryKey: queries.followedProducts.list._def,
      });

      queryClient.invalidateQueries({
        queryKey: queries.products.details(product.slug, platformId).queryKey,
      });
    },
  });
};
